import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import PlaceIcon from '@mui/icons-material/Place'
import DirectionsIcon from '@mui/icons-material/Directions'
import { useTranslation } from 'react-i18next'

import { DIRECTIONS_URL, GOOGLE_LISTING_URL, mapEmbedUrl } from '../../content/presence'
import { useLocale } from '../../i18n/LocaleProvider'

/**
 * The estate on a map, with the two things a guest actually does next.
 *
 * "Open in Google Maps" goes to the listing itself - name, rating, the reviews, guests' photos -
 * not to a search. "Directions" routes to Google's own pin for that listing, which is what the
 * guest's phone will navigate to anyway.
 */
export function LocationMap({ height = { xs: 260, md: 380 } }: { height?: { xs: number; md: number } }) {
  const { t } = useTranslation()
  const { locale } = useLocale()

  return (
    <Card sx={{ overflow: 'hidden' }}>
      <Box
        component="iframe"
        title={t('contact.mapTitle')}
        src={mapEmbedUrl(locale)}
        // Lazy so the map never costs a guest on mobile data who came for the prices.
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
        sx={{
          display: 'block',
          width: '100%',
          height,
          border: 0,
          // The embed is always left-to-right; mirroring it in Hebrew would mirror Finland.
          direction: 'ltr',
        }}
      />
      <Stack direction="row" spacing={1} sx={{ p: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Button
          href={GOOGLE_LISTING_URL}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<PlaceIcon />}
          variant="outlined"
          size="small"
        >
          {t('contact.openInMaps')}
        </Button>
        <Button
          href={DIRECTIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<DirectionsIcon />}
          variant="contained"
          size="small"
        >
          {t('contact.directions')}
        </Button>
      </Stack>
    </Card>
  )
}
